import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Bug, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { logger } from '@/utils/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
      showDetails: false,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Solo log críticos en desarrollo
    if (import.meta.env.DEV) {
      logger.warn('Error capturado:', error.message);
    }
    
    this.setState({
      error,
      errorInfo,
    });

    // Log simplificado
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Throttled logging simplificado
    const errorKey = `error:${error.message.substring(0, 30)}`;
    
    const now = Date.now();
    const storageKey = `error_throttle_${errorKey}`;
    
    let lastLogged: string | null = null;
    try {
      lastLogged = localStorage.getItem(storageKey);
    } catch (e) {
      // Ignorar errores de localStorage silenciosamente
      return;
    }
    
    // Throttle: 30 minutos para errores similares
    if (!lastLogged || now - parseInt(lastLogged) > 1800000) { 
      try {
        localStorage.setItem(storageKey, now.toString());
      } catch (e) {
        // Ignorar errores de localStorage
        return;
      }
      
      // Solo log mínimo en desarrollo
      if (import.meta.env.DEV) {
        logger.warn('🔸 Error:', error.message.substring(0, 50));
      }
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
  };

  private getErrorType = (error: Error): string => {
    if (error.name.includes('Chunk')) return 'CHUNK_LOAD_ERROR';
    if (error.message.includes('Network')) return 'NETWORK_ERROR';
    if (error.message.includes('Cannot read properties')) return 'NULL_REFERENCE_ERROR';
    if (error.message.includes('supabase')) return 'DATABASE_ERROR';
    if (error.message.includes('fetch')) return 'API_ERROR';
    return 'JAVASCRIPT_ERROR';
  };

  private getErrorSolution = (errorType: string): string => {
    const solutions = {
      CHUNK_LOAD_ERROR: 'Recarga la página (F5) - hay una nueva versión de la aplicación disponible.',
      NETWORK_ERROR: 'Verifica tu conexión a internet y vuelve a intentar.',
      NULL_REFERENCE_ERROR: 'Un dato esperado está vacío. Verifica que hayas completado todos los campos requeridos.',
      DATABASE_ERROR: 'Error de conexión con la base de datos. Verifica tu conexión e intenta nuevamente.',
      API_ERROR: 'Error de comunicación con el servidor. Intenta recargar la página.',
      JAVASCRIPT_ERROR: 'Error inesperado en la aplicación. Recarga la página e intenta nuevamente.',
    };
    
    return solutions[errorType as keyof typeof solutions] || solutions.JAVASCRIPT_ERROR;
  };

  public render() {
    if (this.state.hasError && this.state.error) {
      const errorType = this.getErrorType(this.state.error);
      const solution = this.getErrorSolution(errorType);

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-muted/20">
          <Card className="w-full max-w-2xl">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-destructive/10 rounded-full">
                  <AlertTriangle className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle className="text-2xl">¡Oops! Algo salió mal</CardTitle>
              <CardDescription>
                Se produjo un error inesperado en la aplicación
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="destructive">{errorType}</Badge>
                </div>
                <p className="text-sm font-medium text-foreground mb-2">
                  {this.state.error.message}
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Solución recomendada:</strong> {solution}
                </p>
              </div>

              <div className="flex flex-wrap gap-3 justify-center">
                <Button onClick={this.handleReload} className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Recargar Página
                </Button>
                
                <Button variant="outline" onClick={this.handleGoHome} className="flex items-center gap-2">
                  <Home className="h-4 w-4" />
                  Ir al Inicio
                </Button>
                
                <Button variant="outline" onClick={this.handleReset} className="flex items-center gap-2">
                  Intentar Continuar
                </Button>
                
                <Button 
                  variant="ghost" 
                  onClick={() => this.setState({ showDetails: true })}
                  className="flex items-center gap-2"
                >
                  <Bug className="h-4 w-4" />
                  Ver Detalles
                </Button>
              </div>

              <div className="text-center text-sm text-muted-foreground">
                <p>Si el problema persiste, contacta al soporte técnico</p>
                <p className="text-xs mt-1">Error ID: {Date.now().toString(36)}</p>
              </div>
            </CardContent>
          </Card>

          <Dialog open={this.state.showDetails} onOpenChange={(open) => this.setState({ showDetails: open })}>
            <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[80vh] overflow-y-auto p-4 sm:p-6" aria-describedby="error-boundary-description">
              <DialogHeader>
                <DialogTitle>Detalles del Error</DialogTitle>
                <DialogDescription id="error-boundary-description">
                  Información técnica para debugging (útil para reportar el error)
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Mensaje de Error:</h4>
                  <code className="block p-3 bg-muted rounded text-sm">
                    {this.state.error.message}
                  </code>
                </div>

                {this.state.error.stack && (
                  <div>
                    <h4 className="font-semibold mb-2">Stack Trace:</h4>
                    <code className="block p-3 bg-muted rounded text-xs whitespace-pre-wrap">
                      {this.state.error.stack}
                    </code>
                  </div>
                )}

                {this.state.errorInfo?.componentStack && (
                  <div>
                    <h4 className="font-semibold mb-2">Component Stack:</h4>
                    <code className="block p-3 bg-muted rounded text-xs whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </code>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold mb-2">Información del Navegador:</h4>
                  <code className="block p-3 bg-muted rounded text-xs">
                    [Información del navegador ocultada por seguridad]
                  </code>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">URL:</h4>
                  <code className="block p-3 bg-muted rounded text-xs">
                    {window.location.href}
                  </code>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;